import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, ListGroup, ListRow, Skeleton } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import { usePrefs } from '../prefs.tsx';
import { CopyText } from '../servers-ui.tsx';
import { openPayUrl } from '../telegram.ts';
import {
  type ClientPlatform,
  guessClientPlatform,
  QrCode,
  type VpnApp,
  VpnAppGuide,
  VpnAppsList,
} from '../vpn.tsx';

export function VpnTrialScreen() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { t, dateTime } = usePrefs();
  const [guideApp, setGuideApp] = useState<VpnApp | null>(null);
  const [clientPlatform, setClientPlatform] = useState<ClientPlatform>(guessClientPlatform);
  const trial = useQuery(trpc.vpnTrial.get.queryOptions());
  const issue = useMutation(
    trpc.vpnTrial.issue.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(trpc.vpnTrial.get.pathFilter());
      },
    }),
  );

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">{t.trialVpn.title}</h1>

      {trial.isPending ? (
        <Skeleton height={160} />
      ) : trial.isError ? (
        <div className="ul-error" role="alert">
          {errorText(trial.error)}
        </div>
      ) : trial.data?.status === 'active' && trial.data.key ? (
        guideApp?.id ? (
          <VpnAppGuide app={guideApp} vpnKey={trial.data.key} onBack={() => setGuideApp(null)} />
        ) : (
          <div className="ul-stack">
            <div className="ul-vpn-key">
              <QrCode text={trial.data.key} label={t.vpn.qr} />
              <div className="ul-vpn-key__body">
                <span className="ul-vpn-key__title">
                  <Badge tone="ok">{t.vpn.ready}</Badge>
                </span>
                <span className="ul-vpn-key__text">{t.vpn.scanLink}</span>
                <CopyText text={trial.data.key} />
              </div>
            </div>
            <ListGroup header={t.trialVpn.key} footer={t.trialVpn.validUntil(dateTime(trial.data.expiresAt))}>
              <ListRow
                icon="key"
                title={<span className="ul-mono ul-wrap ul-clamp">{trial.data.key}</span>}
                trailing={<CopyText text={trial.data.key} />}
              />
            </ListGroup>
            <VpnAppsList
              kind="vless"
              platform={clientPlatform}
              onPlatformChange={setClientPlatform}
              onSelect={setGuideApp}
            />
            <Button
              size="l"
              block
              icon="shield"
              onClick={() => openPayUrl(new URL('/vpn-status', window.location.origin).toString())}
            >
              {t.trialVpn.check}
            </Button>
          </div>
        )
      ) : trial.data?.status === 'expired' ? (
        <ListGroup>
          <ListRow icon="clock" title={t.trialVpn.expired} subtitle={t.trialVpn.expiredText} />
        </ListGroup>
      ) : (
        <div className="ul-stack">
          <ListGroup>
            <ListRow icon="shield" title={t.trialVpn.intro} subtitle={t.trialVpn.terms} />
          </ListGroup>
          {issue.isError ? (
            <div className="ul-error" role="alert">
              {errorText(issue.error)}
            </div>
          ) : null}
          <Button size="l" block icon="key" loading={issue.isPending} onClick={() => issue.mutate()}>
            {t.trialVpn.issue}
          </Button>
        </div>
      )}
    </main>
  );
}
